import type { IHomeLocation } from '@/domain/entities/home-location';
import { type IProperty } from '@/domain/entities/property';
import type { PropertyData } from '@/domain/entities/property-object';
import type { IHomeSystem } from '@/domain/entities/system';
import { BillFrequency, BillStatus, type FrequencyInput, type IUtility } from '@/domain/entities/utility';
import { AddressVerificationLevel } from '@/domain/shared/address';
import type { IHomeObject } from '@/domain/shared/home-object';
import type { TypeNames } from '@/domain/shared/storable';

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

function makeTmpId(prefix: string): string {
  return `tmp-${prefix}-${crypto.randomUUID()}`;
}

function hydrateHomeObjectBase<T extends IHomeObject>(
  partial: Omit<T, keyof IHomeObject> & Partial<IHomeObject>,
  typeName: TypeNames,
  createdAt = nowSec(),
): T {
  return {
    id: partial.id ?? makeTmpId(typeName.toLowerCase()),
    typeName,
    createdAt: partial.createdAt ?? createdAt,
    updatedAt: partial.updatedAt ?? createdAt,

    title: partial.title,
    description: partial.description ?? null,
    thumbnail: partial.thumbnail ?? null,
    thumbnailUrl: partial.thumbnailUrl ?? null,
    photos: partial.photos ?? null,

    ...partial,
  } as T;
}

function hydrateProperty(
  seed: IProperty,
  currentUserID: string,
  orgID?: string,
  propertyID = makeTmpId('property'),
): IProperty {
  return hydrateHomeObjectBase<IProperty>(
    {
      ...seed,
      id: propertyID,
      orgID: seed.orgID ?? orgID,
      address: {
        ...seed.address,
        addressVerificationLevel: seed.address?.addressVerificationLevel ?? AddressVerificationLevel.LEVEL_0,
      },
      status: seed.status ?? 'builderDraft',
      handOverMeta: seed.handOverMeta ?? {
        createdByUserID: currentUserID,
        createdByOrgID: orgID,
      },
    },
    'Property',
  );
}

function hydrateLocations(seed: readonly IHomeLocation[], propertyID: string): IHomeLocation[] {
  const t = nowSec();

  return seed.map((loc) =>
    hydrateHomeObjectBase<IHomeLocation>(
      {
        ...loc,
        propertyID,
        parentID: loc.parentID ?? propertyID,
        area: loc.area,
      },
      'Location',
      t,
    ),
  );
}

function hydrateUtilities(seed: readonly IUtility[], propertyID: string): IUtility[] {
  const t = nowSec();

  return seed.map((u) =>
    hydrateHomeObjectBase<IUtility>(
      {
        ...u,
        propertyID,
        frequency: u.frequency ?? ({ frequency: BillFrequency.Monthly } satisfies FrequencyInput),
        account: u.account ?? null,
        payeeID: u.payeeID ?? null,
        dueDate: u.dueDate ?? null,
        status: u.status ?? BillStatus.Unpaid,
        paymentMethod: u.paymentMethod ?? null,
        lastPayment: u.lastPayment ?? null,
      },
      'Bill',
      t,
    ),
  );
}

function hydrateSystems(seed: readonly IHomeSystem[], propertyID: string): IHomeSystem[] {
  const t = nowSec();

  return seed.map((s) =>
    hydrateHomeObjectBase<IHomeSystem>(
      {
        ...s,
        propertyID,
        configuration: s.configuration,
      },
      'System',
      t,
    ),
  );
}

/**
 * Hydrate template seed data into full PropertyData ready for editing/saving.
 * - ensures consistent metadata for seeded items
 * - ensures propertyID/parentID links
 */
export function hydrateSeededPropertyData(args: {
  seed: PropertyData;
  currentUserID: string;
  orgID?: string;
}): PropertyData {
  const propertyID = args.seed.property?.id ?? makeTmpId('property');

  return {
    templateID: args.seed.templateID,
    property: args.seed.property
      ? hydrateProperty(args.seed.property, args.currentUserID, args.orgID, propertyID)
      : undefined,

    locations: args.seed.locations ? hydrateLocations(args.seed.locations, propertyID) : [],

    utilities: args.seed.utilities ? hydrateUtilities(args.seed.utilities, propertyID) : [],

    systems: args.seed.systems ? hydrateSystems(args.seed.systems, propertyID) : [],
  };
}
export function buildChildrenIndex(locations: readonly IHomeLocation[]): Map<string, string[]> {
  const byParent = new Map<string, string[]>();

  for (const loc of locations) {
    if (!loc.id) continue;
    const parentId = loc.parentID;
    if (!parentId) continue;

    const arr = byParent.get(parentId) ?? [];
    arr.push(loc.id);
    byParent.set(parentId, arr);
  }

  return byParent;
}

export function collectDescendants(args: { startId: string; childrenByParent: Map<string, string[]> }): Set<string> {
  const out = new Set<string>();
  const stack: string[] = [...(args.childrenByParent.get(args.startId) ?? [])];

  while (stack.length) {
    const id = stack.pop();
    if (!id) continue;
    if (out.has(id)) continue;

    out.add(id);

    const kids = args.childrenByParent.get(id);
    if (kids) stack.push(...kids);
  }

  return out;
}
